/* eslint-disable @typescript-eslint/no-restricted-imports */
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import GlobalLayout from '~/common/components/GlobalLayout';
import QuestionLibraryGlobalLayout from '~/common/components/QuestionLibrary/layouts/QuestionLibraryGlobalLayout';
import { useUser } from '~/common/hooks/useAuthentication';
import useGetLoanSummary, {
  type LoanSummary,
} from '~/common/hooks/useGetLoanSummary';
import { useSelectedAlert } from '~/stores/navigationStore';

import {
  TaxesSummaryFormValues,
  formatTaxSummarySaveData,
  getTaxSummaryInitialValues,
  getTaxSummaryScreenData,
  useDocumentOptions,
} from './common/helpers';
import {
  useMillageRateSideEffect,
  useShockLetterDisableSideEffect,
  useTaxRateSideEffect,
} from './common/hooks/taxesSummarySideEffects';
import { useTaxesSummaryData } from './common/hooks/useTaxesSummaryData';
import json from './layouts/taxesSummary.json';

export default function TaxesSummary() {
  const { isLoading, isError, data, saveTaxSummary } = useTaxesSummaryData();
  const {
    data: loanData,
    isLoading: isLoanSummaryLoading,
    isError: loanSummaryError,
  } = useGetLoanSummary();

  if (
    isLoading ||
    isError ||
    !data ||
    isLoanSummaryLoading ||
    loanSummaryError
  ) {
    return (
      <GlobalLayout
        isLoading={isLoading || isLoanSummaryLoading}
        error={
          (isError || loanSummaryError) && 'There was an issue loading Taxes'
        }
        primaryButton={{
          isDisabled: true,
        }}
      />
    );
  }

  return (
    <SummaryContent
      saveTaxSummary={saveTaxSummary}
      data={data}
      loanSummaryInfo={loanData}
    />
  );
}

type TaxesSummaryContentProps = {
  data: ReturnType<typeof useTaxesSummaryData>['data'];
  loanSummaryInfo: LoanSummary;
  saveTaxSummary: (
    data: Parameters<
      ReturnType<typeof useTaxesSummaryData>['saveTaxSummary']
    >[0],
  ) => void;
};

const SummaryContent = ({
  saveTaxSummary,
  data,
  loanSummaryInfo,
}: TaxesSummaryContentProps) => {
  const displayTitle = useSelectedAlert((alert) => alert.displayTitle);
  const { isUnderwriter } = useUser();

  const methods = useForm({
    mode: 'onBlur',
  });

  const { documentId, documentOptions } = useDocumentOptions(
    data?.taxDocuments,
  );

  useEffect(() => {
    if (data) {
      methods.reset(getTaxSummaryInitialValues(data));
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [data]);

  const screenData = useMemo(() => {
    const canEditFields = isUnderwriter;
    return {
      ...getTaxSummaryScreenData(loanSummaryInfo, data),
      canEditFields,
    };
  }, [loanSummaryInfo, isUnderwriter, data]);

  const onSubmit = async (values: TaxesSummaryFormValues) => {
    const postData = formatTaxSummarySaveData(values);
    saveTaxSummary(postData);
  };

  return (
    <QuestionLibraryGlobalLayout
      headerOptions={{
        title: displayTitle,
        isAiSwitchButtonEnabled: false,
        isHidden: true,
      }}
      questions={json}
      showPdfViewer={true}
      documentId={documentId}
      documentOptions={documentOptions}
      screenData={screenData}
      sideEffects={[
        useTaxRateSideEffect,
        useMillageRateSideEffect,
        useShockLetterDisableSideEffect,
      ]}
      hasNewBackground
      maxHeight={true}
      gridTemplateColumns="50% 50%"
      methods={methods}
      primaryButton={{
        onSubmit,
      }}
      fullPageHeight={true}
    />
  );
};
